import {
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createStitcherAccessClient } from './api';
import type {
  StitcherAccessFilterValue,
  StitcherAccessUser,
  StitcherAccessUserPage,
} from './model';

export const stitcherAccessQueryKey = ['admin', 'stitcher-access'] as const;
const stitcherAccessListKey = [...stitcherAccessQueryKey, 'list'] as const;
const setStitcherAccessMutationKey = [...stitcherAccessQueryKey, 'set'] as const;

interface SetStitcherAccessVariables {
  userId: string;
  useStitcher: boolean;
}

function updateCachedUser(
  queryClient: QueryClient,
  userId: string,
  update: (user: StitcherAccessUser) => StitcherAccessUser,
) {
  queryClient.setQueriesData<StitcherAccessUserPage>({ queryKey: stitcherAccessListKey }, (page) =>
    page
      ? {
          ...page,
          content: page.content.map((user) => (user.id === userId ? update(user) : user)),
        }
      : page,
  );
}

function useStitcherAccessClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createStitcherAccessClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function useStitcherAccessQuery(filter: StitcherAccessFilterValue) {
  const client = useStitcherAccessClient();
  return useQuery({
    queryKey: [...stitcherAccessListKey, filter],
    queryFn: () => client.list(filter),
  });
}

export function useSetStitcherAccessMutation() {
  const client = useStitcherAccessClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: setStitcherAccessMutationKey,
    mutationFn: ({ userId, useStitcher }: SetStitcherAccessVariables) =>
      client.setUseStitcher(userId, useStitcher),
    // Patch only the changed row so the table is not refetched or shown as loading.
    onSuccess: (user) => {
      updateCachedUser(queryClient, user.id, () => user);
    },
  });
}

/** Users with a toggle still in flight; each row tracks its own PUT, not the latest one. */
export function usePendingStitcherAccessUserIds() {
  const userIds = useMutationState({
    filters: { mutationKey: setStitcherAccessMutationKey, status: 'pending' },
    select: (mutation) => (mutation.state.variables as SetStitcherAccessVariables).userId,
  });
  return new Set(userIds);
}
